"use client";

import { atom, useAtom } from "jotai";
import { useEffect, useRef } from "react";

export type BreakPoint = "sm" | "md" | "lg" | "xl" | "2xl";

interface BreakPointMatches {
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  is2xl: boolean;
}

const breakpoints: Record<BreakPoint, string> = {
  sm: "40rem",
  md: "48rem",
  lg: "64rem",
  xl: "80rem",
  "2xl": "96rem",
};

const initialMatches: BreakPointMatches = {
  isSm: false,
  isMd: false,
  isLg: false,
  isXl: false,
  is2xl: false,
};

const breakpointMatchesAtom = atom<BreakPointMatches>(initialMatches);

let isInitialized = false;
let atomUpdateFn: ((matches: BreakPointMatches) => void) | null = null;
let activeHooksCount = 0;
const mediaQueries: Partial<Record<BreakPoint, MediaQueryList>> = {};

const updateAllMatches = () => {
  if (!atomUpdateFn) return;

  const newMatches: BreakPointMatches = {
    isSm: mediaQueries.sm?.matches ?? false,
    isMd: mediaQueries.md?.matches ?? false,
    isLg: mediaQueries.lg?.matches ?? false,
    isXl: mediaQueries.xl?.matches ?? false,
    is2xl: mediaQueries["2xl"]?.matches ?? false,
  };

  atomUpdateFn(newMatches);
};

const initializeMediaQueries = () => {
  if (isInitialized || typeof window === "undefined") return;

  isInitialized = true;

  // Initialize all media queries
  (Object.keys(breakpoints) as BreakPoint[]).forEach((bp) => {
    const resolvedValue =
      getComputedStyle(document.documentElement)
        .getPropertyValue(`--breakpoint-${bp}`)
        .trim() || breakpoints[bp];

    mediaQueries[bp] = window.matchMedia(`(min-width: ${resolvedValue})`);
  });

  updateAllMatches();

  Object.values(mediaQueries).forEach((mediaQuery) => {
    mediaQuery?.addEventListener("change", updateAllMatches);
  });
};

const cleanupMediaQueries = () => {
  if (!isInitialized) return;

  Object.values(mediaQueries).forEach((mediaQuery) => {
    mediaQuery?.removeEventListener("change", updateAllMatches);
  });

  isInitialized = false;
  atomUpdateFn = null;
};

export const useMediaQuery = () => {
  const [matches, setMatches] = useAtom(breakpointMatchesAtom);
  const setMatchesRef = useRef(setMatches);

  // Keep the setter reference up to date
  useEffect(() => {
    setMatchesRef.current = setMatches;
  }, [setMatches]);

  useEffect(() => {
    // Increment reference count
    activeHooksCount++;

    // Register the setter with the global updater (wrapped to always use latest)
    if (!atomUpdateFn) {
      atomUpdateFn = (newMatches) => setMatchesRef.current(newMatches);
    }

    // Initialize media queries once (runs only on first hook mount)
    initializeMediaQueries();

    return () => {
      // Decrement reference count
      activeHooksCount--;

      // Clean up when last component unmounts
      if (activeHooksCount === 0) {
        cleanupMediaQueries();
      }
    };
  }, []);

  return { ...matches, isInitialized };
};
